import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { STAGE, gbp, isOpen } from "@/lib/stages";
import { ukDayYear } from "@/lib/time";
import { CaseStamp } from "@/components/portal/crm/CaseStamp";
import { ApplyLinksPanel } from "@/components/portal/ApplyLinksPanel";
import {
  Btn, CellMain, CellSub, DeskSupport, Divider, Empty, Kv, LinkRow, Money, Note, PageBody, PageHead, Panel, RailCard,
  Sec, StatusChip, StatStrip, Tb, Td, Th,
} from "@/components/portal/crm/kit";

const band = (amt) => {
  const a = Number(amt || 0);
  if (a < 25000) return "Under £25k";
  if (a < 50000) return "£25k – £50k";
  if (a < 100000) return "£50k – £100k";
  if (a < 250000) return "£100k – £250k";
  if (a < 500000) return "£250k – £500k";
  if (a < 1000000) return "£500k – £1m";
  return "£1m+";
};

const range = (c) => {
  if (!c || !c.max_gbp) return null;
  return c.min_gbp === c.max_gbp ? gbp(c.max_gbp) : `${gbp(c.min_gbp)} – ${gbp(c.max_gbp)}`;
};

export default function CampaignHome() {
  const { leads, summary, projection, me } = useOutletContext();
  const nav = useNavigate();
  const [link, setLink] = useState(null);

  useEffect(() => {
    api.get("/refer/apply-link").then((r) => setLink(r.data)).catch(() => setLink(null));
  }, []);

  const byRef = useMemo(() => {
    const m = {};
    (projection?.cases || []).forEach((c) => { if (c.reference) m[c.reference] = c; });
    return m;
  }, [projection]);

  const live = leads.filter((l) => isOpen(l.stage));
  const funded = leads.filter((l) => l.stage === "completed");
  const s = link?.stats || {};
  const t = projection?.totals;
  const projected = t ? (t.min_gbp === t.max_gbp ? gbp(t.max_gbp) : `${gbp(t.min_gbp)} – ${gbp(t.max_gbp)}`) : "—";

  return (
    <>
      <PageHead
        eyebrow="Campaign partner"
        title="Your campaign"
        lede="Everything your reach has produced, what stage it is at, and what it is worth to you if it funds. Connexis works every case; you never hold a client's details."
        testId="campaign-head"
        actions={
          <>
            <Btn onClick={() => nav("/introducer/media-kit")} testId="campaign-media-kit">Media kit & ad rules</Btn>
            <Btn kind="primary" onClick={() => nav("/introducer/channels")} testId="campaign-channels-cta">Channels & tracking</Btn>
          </>
        }
      />

      <PageBody
        rail={
          <>
            <RailCard title="Your earnings" testId="campaign-earnings">
              <Kv rows={[
                ["Owed to you", gbp(summary?.owed_gbp ?? summary?.commission_owed_gbp ?? 0)],
                ["Paid so far", gbp(summary?.paid_gbp ?? summary?.commission_paid_gbp ?? 0)],
                ["If live cases fund", projected],
              ]} />
              <Divider style={{ margin: "18px 0" }} />
              <LinkRow label="Channels & tracking" onClick={() => nav("/introducer/channels")} testId="campaign-rail-channels" />
              <LinkRow label="Earnings in full" onClick={() => nav("/introducer/performance")} testId="campaign-rail-earnings" />
              <LinkRow label="Payout statements" onClick={() => nav("/introducer/statements")} testId="campaign-rail-statements" />
            </RailCard>
            <DeskSupport body="Want approved wording for a new channel, or a landing page with your name on it? Ask the desk." />
            <Note testId="campaign-masking-note">
              You see stages, sizes and your fee. You never see who applied, which desk holds a case, or anything in the file — that stays with Connexis, by design.
            </Note>
          </>
        }
      >
        <StatStrip
          testId="campaign-stats"
          items={[
            { k: "Link opens", v: String((s.views || 0) + (s.funding_views || 0)), h: `${s.funding_views || 0} read the explainer first`, testId: "campaign-stat-opens" },
            { k: "Applications", v: String(s.submitted || 0), h: `${s.started || 0} started`, testId: "campaign-stat-apps" },
            { k: "Live cases", v: String(live.length), h: live.length ? `Worth ${projected} if they fund` : "Nothing live right now", testId: "campaign-stat-live" },
            { k: "Funded", v: String(funded.length), h: funded.length ? "On your statements" : "Not yet", tone: funded.length ? "green" : undefined, testId: "campaign-stat-funded" },
          ]}
        />

        <Sec title="Your campaign links" sub="Every one of these carries your attribution. Put them behind ads, in emails, on print — anything that comes through is yours.">
          <div data-testid="campaign-links"><ApplyLinksPanel campaign /></div>
        </Sec>

        <Sec title="What came through" sub="Each application from your links, masked. Stage and size only — Connexis handles the client.">
          <Panel pad={false} testId="campaign-cases" style={{ overflow: "hidden" }}>
            {leads.length === 0 ? (
              <Empty testId="campaign-cases-empty">Nothing yet. The first application through your link shows here the moment it lands.</Empty>
            ) : (
              <Tb testId="campaign-cases-table" head={<>
                <Th>Case</Th><Th>Finance</Th><Th>Size</Th><Th>Stage</Th><Th>Received</Th><Th className="r">If it funds</Th>
              </>}>
                {leads.map((l, i) => {
                  const st = STAGE[l.stage] || STAGE.new;
                  const p = byRef[l.reference];
                  return (
                    <tr key={l.reference || i} data-testid={`campaign-case-${i}`}>
                      <Td className="lead">
                        <CellMain>{l.label || "Client via your campaign"}</CellMain>
                        <CellSub>{l.routes?.length ? `${l.routes.filter((r) => r.status !== "declined").length} desk${l.routes.filter((r) => r.status !== "declined").length === 1 ? "" : "s"} holding it` : "Not with a desk yet"}</CellSub>
                        <div><CaseStamp at={l.created_at} testId={`campaign-case-stamp-${l.id || l.reference || i}`} /></div>
                      </Td>
                      <Td><CellMain>{l.finance_type || "Business funding"}</CellMain></Td>
                      <Td><Money raw={band(l.amount_gbp)} tone="muted" /></Td>
                      <Td><StatusChip state={st} /></Td>
                      <Td><CellMain>{ukDayYear(l.created_at, "—")}</CellMain></Td>
                      <Td className="r"><Money raw={p?.state === "funded" ? gbp(p.likely_gbp) : (range(p) || "Not priced yet")} tone={p?.max_gbp ? (p.state === "funded" ? "green" : "ink") : "muted"} /></Td>
                    </tr>
                  );
                })}
              </Tb>
            )}
          </Panel>
        </Sec>

        <Sec>
          <Note testId="campaign-projection-note">{projection?.note || "Figures are a range, not a quote, and never a promise."}</Note>
        </Sec>
      </PageBody>
    </>
  );
}
